import type {
  AppSpecificDealPromptWISSPFilters,
  AudienceSpecificDealPromptWISSPFilters,
  BaseDealPromptWISSPFilters,
  DealPromptFeatureFlaggedFilters,
  DomainSpecificDealPromptWISSPFilters,
} from '@openx/types/dealPrompt.ts';
import type {
  AgeRangeInput,
  AudienceSegmentSearchFilters,
  AudienceSegmentSearchFiltersInput,
  InventoryBundleSearchFiltersInput,
  InventoryDomainSearchFiltersInput,
  SegmentSearchMode,
} from '@openx/types/gqlTypes.ts';
import { Gender } from '@openx/types/gqlTypes.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import capitalize from 'lodash/capitalize';
import {
  DEAL_PROMPT_US_REGION_CODE,
  type DealPromptContextType,
  type DealPromptFilterFormat,
  type DealPromptFilterGender,
  type DealPromptFilterGoal,
  type DealPromptFilterPlatform,
  type DealPromptFilterSegmentSearchMode,
  type DealPromptFilters,
  dealPromptContextTypes,
  dealPromptFilterDevice,
  dealPromptFilterFormatWisspMap,
  dealPromptFilterGender,
  dealPromptFilterSegmentSearchMode,
} from '../types.ts';
import { defaultAppSpecificFilters, defaultBaseFilters, defaultDomainSpecificFilters } from './defaultFilters.ts';
import { sanitizeDealPromptFormatAndDevices } from './sanitizeDealPromptFormatAndDevices.ts';

type DomainFiltersBody = BaseDealPromptWISSPFilters & DomainSpecificDealPromptWISSPFilters;
type AppFiltersBody = BaseDealPromptWISSPFilters &
  AppSpecificDealPromptWISSPFilters &
  Pick<DomainSpecificDealPromptWISSPFilters, 'domainFilters'>;
type AudienceFiltersBody = BaseDealPromptWISSPFilters & AudienceSpecificDealPromptWISSPFilters;

type FiltersBody<T = DealPromptContextType> = T extends typeof dealPromptContextTypes.DOMAINS
  ? DomainFiltersBody
  : T extends typeof dealPromptContextTypes.APPS
    ? AppFiltersBody
    : T extends typeof dealPromptContextTypes.AUDIENCE
      ? AudienceFiltersBody
      : never;

export const mapCriteriaToFiltersBody = <T = DealPromptContextType>(
  { filters }: AllTableCriteria<DealPromptFilters>,
  contextType: T,
  featureFlaggedFilters: DealPromptFeatureFlaggedFilters
): FiltersBody<T> => {
  const isDomainContext = contextType === dealPromptContextTypes.DOMAINS;
  const isAppContext = contextType === dealPromptContextTypes.APPS;

  /** BASE FILTERS */
  let noOfResult = defaultBaseFilters.noOfResult;
  if (isDomainContext) {
    noOfResult = featureFlaggedFilters.domainsNumberOfResults;
  } else if (isAppContext) {
    noOfResult = featureFlaggedFilters.bundlesNumberOfResults;
  }

  /** PERFORMANCE FILTERS */
  let performanceDataFilters = defaultBaseFilters.performance.performanceData;
  if (isDomainContext) {
    performanceDataFilters = [
      {
        filterColumn: 'cprmarketrequests',
        filterThreshold: featureFlaggedFilters.domainsCprMarketRequestsThreshold,
      },
      {
        filterColumn: 'impressions',
        filterThreshold: featureFlaggedFilters.domainsImpressionsThreshold,
      },
    ];
  } else if (isAppContext) {
    performanceDataFilters = [
      {
        filterColumn: 'cprmarketrequests',
        filterThreshold: featureFlaggedFilters.bundlesCprMarketRequestsThreshold,
      },
      {
        filterColumn: 'impressions',
        filterThreshold: featureFlaggedFilters.bundlesImpressionsThreshold,
      },
    ];
  }
  if (filters?.ds_impressions) {
    performanceDataFilters = [
      ...performanceDataFilters.filter(f => f.filterColumn !== 'impressions'),
      { filterColumn: 'impressions', filterThreshold: Number(filters.ds_impressions) },
    ];
  }

  /** CUMULATIVE DATA FILTERS */
  const kpiMap: Record<DealPromptFilterGoal, string> = {
    spend: 'total_bidspendinusd',
    impressions: 'total_impressions',
    ctr: 'avg_clickthroughrate',
  };
  const cumulativePerformanceData = filters?.goal && kpiMap[filters.goal.toLowerCase()];

  return {
    ...defaultBaseFilters,
    noOfResult,
    performance: {
      ...defaultBaseFilters.performance,
      ...(filters?.ds_viewability && { minViewability: Number(filters.ds_viewability) }),
      performanceData: [
        ...performanceDataFilters,
        ...(filters?.ds_cpm ? [{ filterColumn: 'bidCpm', filterThreshold: Number(filters.ds_cpm) * 1_000_000 }] : []),
      ],
    },
    cumulativeDataFilters: {
      ...defaultBaseFilters.cumulativeDataFilters,
      marketThreshold: featureFlaggedFilters.marketThreshold,
      ...(cumulativePerformanceData && { performanceData: cumulativePerformanceData }),
    },

    ...(isDomainContext && {
      ...defaultDomainSpecificFilters,
      vectorFilters: {
        ...defaultDomainSpecificFilters.vectorFilters,
        ...{ iabCategory: filters?.ds_content_vertical || [] },
      },
    }),

    ...(isAppContext && {
      ...defaultAppSpecificFilters,
      appFilters: {
        ...defaultAppSpecificFilters.appFilters,
        category: filters?.ds_content_vertical || [],
        platform: filters?.platform || [],
      },
    }),

    ...((isDomainContext || isAppContext) && {
      domainFilters: {
        ...defaultDomainSpecificFilters.domainFilters,
        ...(filters?.geography && { geography: filters.geography.map(g => g.toUpperCase()) }),
        ...(filters?.format && {
          mediaSubtype: filters.format.map(f => capitalize(dealPromptFilterFormatWisspMap[f] || f.toLowerCase())),
        }),
        ...(filters?.ds_is_direct && {
          isDirect: filters?.ds_is_direct ? true : null,
        }),
        ...(filters?.ds_device && {
          deviceType: filters.ds_device
            .filter(filterOption => (Object.values(dealPromptFilterDevice) as string[]).includes(filterOption))
            .map(d => d.toLowerCase()),
        }),
        ...(filters?.ds_video_placement && { videoPlcmt: filters.ds_video_placement.map(v => v.toUpperCase()) }),
      },
    }),
  } as FiltersBody<T>;
};

export const mapFiltersBodyToCriteria = <T = DealPromptContextType>(
  filtersBody: InventoryBundleSearchFiltersInput | InventoryDomainSearchFiltersInput,
  contextType: T
): DealPromptFilters => {
  const isDomainContext = contextType === dealPromptContextTypes.DOMAINS;
  const isAppContext = contextType === dealPromptContextTypes.APPS;

  const filters: Partial<DealPromptFilters> = {};

  // BASE FILTERS
  if (
    (filtersBody as InventoryBundleSearchFiltersInput).performance?.minViewability !== undefined &&
    filtersBody.performance?.minViewability !== 0
  ) {
    filters.ds_viewability = `${filtersBody.performance.minViewability}`;
  }

  // PERFORMANCE FILTERS
  if (Array.isArray(filtersBody.performance?.performanceData)) {
    const impressionsFilter = filtersBody.performance.performanceData.find(f => f.filterColumn === 'impressions');
    if (impressionsFilter && impressionsFilter.filterThreshold !== undefined) {
      filters.ds_impressions = `${impressionsFilter.filterThreshold}`;
    }
    const cpmFilter = filtersBody.performance.performanceData.find(f => f.filterColumn === 'bidCpm');
    if (cpmFilter && cpmFilter.filterThreshold !== undefined) {
      filters.ds_cpm = `${cpmFilter.filterThreshold / 1_000_000}`;
    }
  }

  // CUMULATIVE DATA FILTERS
  if (filtersBody.cumulativeDataFilters?.performanceData) {
    const kpiReverseMap: Record<string, DealPromptFilterGoal> = {
      total_bidspendinusd: 'spend',
      total_impressions: 'impressions',
      avg_clickthroughrate: 'ctr',
    };
    const goalKey = filtersBody.cumulativeDataFilters.performanceData;
    if (goalKey && kpiReverseMap[goalKey]) {
      filters.goal = kpiReverseMap[goalKey];
    }
  }

  // DOMAIN CONTEXT
  if (isDomainContext && (filtersBody as DomainFiltersBody).vectorFilters?.iabCategory) {
    filters.ds_content_vertical = (filtersBody as DomainFiltersBody).vectorFilters.iabCategory;
  }

  // APP CONTEXT
  if (isAppContext && (filtersBody as AppFiltersBody).appFilters?.category) {
    filters.ds_content_vertical = (filtersBody as AppFiltersBody).appFilters.category;
  }
  if (isAppContext && (filtersBody as AppFiltersBody).appFilters?.platform) {
    filters.platform = (filtersBody as AppFiltersBody).appFilters.platform as DealPromptFilterPlatform[];
  }

  // DOMAIN FILTERS (shared)
  if ((isDomainContext || isAppContext) && filtersBody.domainFilters) {
    if (filtersBody.domainFilters.geography?.length) {
      filters.geography = filtersBody.domainFilters.geography.map(g => g.toUpperCase());
    }
    if (filtersBody.domainFilters.mediaSubtype) {
      // Reverse map: try to get original format from mediaSubtype
      filters.format = filtersBody.domainFilters.mediaSubtype.map(f => {
        // Try to reverse map using dealPromptFilterFormatWisspMap
        const entry = Object.entries(dealPromptFilterFormatWisspMap).find(
          ([, v]) => capitalize(v || f.toLowerCase()) === f
        );
        return entry ? entry[0] : f.toLowerCase();
      }) as DealPromptFilterFormat[];
    }
    if (filtersBody.domainFilters.isDirect !== undefined) {
      filters.ds_is_direct = filtersBody.domainFilters.isDirect ? ['true'] : undefined;
    }
    if (filtersBody.domainFilters.deviceType) {
      filters.ds_device = filtersBody.domainFilters.deviceType.map(d => d.toLowerCase());
    }
    if (filtersBody.domainFilters.videoPlcmt?.length) {
      filters.ds_video_placement = filtersBody.domainFilters.videoPlcmt.map(v => v.toUpperCase());
    }
  }

  return sanitizeDealPromptFormatAndDevices(filters);
};

const mapSegmentSearchModeToFiltersBody = (
  segmentSearchMode: DealPromptFilterSegmentSearchMode | undefined
): SegmentSearchMode | undefined => {
  if (segmentSearchMode === dealPromptFilterSegmentSearchMode.ALL || !segmentSearchMode) {
    return undefined;
  }
  return segmentSearchMode;
};

const parseAgeBound = (value: string | undefined): number | null => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const mapAgeToAgeRange = (age: string[] | undefined): AgeRangeInput | undefined => {
  if (!Array.isArray(age) || age.length !== 2) return undefined;

  const [minStr, maxStr] = age;
  const min = parseAgeBound(minStr);
  const max = parseAgeBound(maxStr);

  if (min === null && max === null) return undefined;

  return { min, max };
};

const mapRegionToFiltersBody = (region: DealPromptFilters['region']): string | undefined => {
  if (!region || region === DEAL_PROMPT_US_REGION_CODE) {
    return undefined;
  }

  return region;
};

const mapGender = (gender: DealPromptFilterGender | undefined) => {
  if (gender === dealPromptFilterGender.ANY || !gender) {
    return undefined;
  }
  return gender;
};

export const mapAudienceCriteriaToFiltersBody = (
  criteria: AllTableCriteria<DealPromptFilters>
): AudienceSegmentSearchFiltersInput => {
  const rawReach = criteria.filters?.uniqueUsersReach;
  const normalizedReach =
    rawReach !== undefined && rawReach !== null
      ? typeof rawReach === 'string'
        ? rawReach.replace(/_/g, '')
        : rawReach
      : undefined;
  const goalUniqueUsersReach = normalizedReach !== undefined ? Number(normalizedReach) : undefined;
  const reachInt =
    goalUniqueUsersReach !== undefined && Number.isFinite(goalUniqueUsersReach)
      ? Math.floor(goalUniqueUsersReach)
      : undefined;

  return {
    userLocationStates: criteria.filters?.state?.length ? [...criteria.filters.state] : undefined,
    userLocationDMAs: criteria.filters?.dma?.length ? [...criteria.filters.dma] : undefined,
    country: criteria.filters?.country,
    region: mapRegionToFiltersBody(criteria.filters?.region),
    demographicsAge: mapAgeToAgeRange(criteria.filters?.age),
    demographicsGender: mapGender(criteria.filters?.gender),
    goalUniqueUsersReach: reachInt,
    segmentsCategories: criteria.filters?.category?.map(category => category),
    segmentsSubCategories: criteria.filters?.sub_category?.map(subCategory => subCategory),
    segmentsDataPartners: criteria.filters?.account?.map(account => account),
    segmentsSearchMode: mapSegmentSearchModeToFiltersBody(criteria.filters?.segment_search_mode),
  };
};

/**
 * Maps `GetAudienceSegmentSearchText` / `generatedSearchInput.filters` into Discovery Studio filter criteria
 * so the filter bar reflects what the service interpreted (merged on top of any existing criteria keys).
 */
export const mapAudienceSearchFiltersToCriteria = (
  filters: AudienceSegmentSearchFilters | null | undefined
): DealPromptFilters => {
  if (!filters) return {};

  const out: DealPromptFilters = {};

  const state = filters.userLocationStates?.length ? [...filters.userLocationStates] : undefined;
  if (state) out.state = state;

  const dma = filters.userLocationDMAs?.length ? [...filters.userLocationDMAs] : undefined;
  if (dma) out.dma = dma;

  if (filters.country != null && filters.country.length > 0) {
    out.country = [...filters.country];
  }

  const congressionalDistrict = filters.userLocationCongressionalDistricts?.length
    ? [...filters.userLocationCongressionalDistricts]
    : undefined;

  if (congressionalDistrict) out.congressionalDistrict = congressionalDistrict;

  if (filters.demographicsAge != null) {
    const { min, max } = filters.demographicsAge;
    const hasMin = typeof min === 'number';
    const hasMax = typeof max === 'number';

    if (hasMin || hasMax) {
      out.age = [hasMin ? String(min) : '', hasMax ? String(max) : ''];
    }
  }

  if (filters.demographicsGender === Gender.Male || filters.demographicsGender === Gender.Female) {
    out.gender = filters.demographicsGender;
  }

  if (filters.goalUniqueUsersReach != null && Number.isFinite(filters.goalUniqueUsersReach)) {
    out.uniqueUsersReach = String(filters.goalUniqueUsersReach / 1000000);
  }

  if (filters.segmentsCategories != null && filters.segmentsCategories.length > 0) {
    out.category = [...filters.segmentsCategories];
  }

  if (filters.segmentsSubCategories != null && filters.segmentsSubCategories.length > 0) {
    out.sub_category = [...filters.segmentsSubCategories];
  }

  if (filters.segmentsDataPartners != null && filters.segmentsDataPartners.length > 0) {
    out.account = [...filters.segmentsDataPartners];
  }

  if (filters.segmentsSearchMode != null) {
    out.segment_search_mode = filters.segmentsSearchMode as DealPromptFilterSegmentSearchMode;
  }

  if (filters.region) {
    out.region = filters.region;
  }

  return sanitizeDealPromptFormatAndDevices(out);
};
